import type {
  MyIssuedRosterResponse,
  MyIssuedRosterScheduleEntry,
} from "@/hooks/useMyIssuedRoster";
import type { Roster, RosterScheduleDayCell } from "@vTypes/snapshot-type";

function isNewScheduleEntry(v: unknown): v is MyIssuedRosterScheduleEntry {
  return !!(
    v &&
    typeof v === "object" &&
    "day" in v &&
    typeof (v as { day: unknown }).day === "number" &&
    "code" in v &&
    typeof (v as { code: unknown }).code === "string"
  );
}

/** API `schedule` 원소를 `{ code, color }` 셀 배열로 정규화 */
function mapScheduleToDayCells(raw: unknown): RosterScheduleDayCell[] {
  const arr = Array.isArray(raw) ? raw : [];
  return arr.map((cell): RosterScheduleDayCell => {
    if (isNewScheduleEntry(cell)) {
      return { code: cell.code, color: cell.color ?? "" };
    }
    if (typeof cell === "string") return { code: cell, color: "" };
    if (
      cell &&
      typeof cell === "object" &&
      "code" in cell &&
      typeof (cell as { code: unknown }).code === "string"
    ) {
      const o = cell as { code: string; color?: string };
      return { code: o.code, color: typeof o.color === "string" ? o.color : "" };
    }
    return { code: "", color: "" };
  });
}

/** `schedule_ids`는 신규 스펙이면 schedule[].schedule_id, 없으면 구버전 루트값 사용 */
function mapScheduleIds(
  my: MyIssuedRosterResponse
): (number | null)[] | null {
  const arr = Array.isArray(my.schedule) ? my.schedule : [];
  const hasNewShape = arr.some((v) => isNewScheduleEntry(v));
  if (hasNewShape) {
    return arr.map((v) => (isNewScheduleEntry(v) ? v.schedule_id ?? null : null));
  }
  return my.schedule_ids ?? null;
}

/**
 * GET /roster/issued_roster/me 응답을 ViewRosterCalendar 등이 기대하는 `Roster` 형태로 변환.
 * `nurses`는 본인 1명만 포함(동료 셀 D/E/N 비교는 빈 배열).
 */
export function myIssuedRosterResponseToRoster(
  my: MyIssuedRosterResponse,
  current: { year: number; month: number }
): Roster {
  const scheduleCells = mapScheduleToDayCells(my.schedule as unknown[]);
  return {
    year: my.year ?? current.year,
    month: my.month ?? current.month,
    days_in_month: scheduleCells.length,
    shift_colors: my.shift_colors ?? {},
    nurses: [
      {
        nurse_id: my.nurse_id,
        name: my.name,
        experience: 0,
        schedule: scheduleCells,
        schedule_ids: mapScheduleIds(my),
        counts: my.counts ?? {},
      },
    ],
  };
}
